import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { THEME } from '../../utils/theme';
import api from '../../utils/api';
import Spinner from '../ui/Spinner';
import Avatar from '../ui/Avatar';
import Badge from '../ui/Badge';

const QueueModal = ({ onClose, queue, onRemove, onReorder, onShowDetail, currentSquad, currentUser }) => {
  const [items, setItems] = useState(queue);
  const [draggedIdx, setDraggedIdx] = useState(null);
  const [activeTab, setActiveTab] = useState('mine');
  const [squadQueues, setSquadQueues] = useState([]);
  const [loadingSquad, setLoadingSquad] = useState(false);

  useEffect(() => { setItems(queue); }, [queue]);

  useEffect(() => {
    if (currentSquad && activeTab === 'squad') {
      setLoadingSquad(true);
      api.get(`/api/queue/squad/${currentSquad.id}`)
        .then(setSquadQueues)
        .catch(console.error)
        .finally(() => setLoadingSquad(false));
    }
  }, [currentSquad, activeTab]);

  const handleDragStart = (idx) => setDraggedIdx(idx);
  const handleDragOver = (e, idx) => {
    e.preventDefault();
    if (draggedIdx === null || draggedIdx === idx) return;
    const newItems = [...items];
    const [dragged] = newItems.splice(draggedIdx, 1);
    newItems.splice(idx, 0, dragged);
    setItems(newItems);
    setDraggedIdx(idx);
  };
  const handleDragEnd = async () => {
    setDraggedIdx(null);
    const gameIds = items.map(i => i.game_id);
    try {
      await api.put('/api/queue/reorder', { game_ids: gameIds });
      if (onReorder) onReorder();
    } catch (err) { console.error(err); }
  };

  const renderQueueItem = (item, idx, canDrag = false, canRemove = false) => (
    <div
      key={item.id || idx}
      draggable={canDrag}
      onDragStart={canDrag ? () => handleDragStart(idx) : undefined}
      onDragOver={canDrag ? (e) => handleDragOver(e, idx) : undefined}
      onDragEnd={canDrag ? handleDragEnd : undefined}
      style={{
        display: 'flex', gap: '12px', alignItems: 'center', padding: '12px',
        background: draggedIdx === idx ? `${THEME.accent}20` : THEME.bg,
        borderRadius: '12px', cursor: canDrag ? 'grab' : 'default', border: `1px solid ${THEME.border}`,
        transition: 'all 0.15s'
      }}
    >
      {canDrag && <span style={{ color: THEME.textDim, fontSize: '16px' }}>⋮⋮</span>}
      <span style={{ color: idx === 0 ? THEME.accent : THEME.textMuted, fontWeight: '700', fontSize: '14px', width: '24px', textAlign: 'center' }}>
        {idx === 0 ? '🎯' : idx + 1}
      </span>
      <img
        src={item.game.header_image}
        alt=""
        style={{ width: '80px', height: '45px', objectFit: 'cover', borderRadius: '8px', cursor: 'pointer' }}
        onClick={() => onShowDetail && onShowDetail(item.game)}
      />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ color: THEME.text, fontSize: '14px', fontWeight: '600', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.game.name}</div>
      </div>
      {canRemove && (
        <button onClick={() => onRemove(item.game_id)} style={{ background: `${THEME.danger}15`, color: THEME.danger, border: `1px solid ${THEME.danger}30`, borderRadius: '8px', padding: '6px 12px', fontSize: '12px', cursor: 'pointer', fontWeight: '500' }}>✕</button>
      )}
    </div>
  );

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      style={{ position: 'fixed', inset: 0, background: 'rgba(5,5,10,0.88)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, backdropFilter: 'blur(32px) saturate(180%)', WebkitBackdropFilter: 'blur(32px) saturate(180%)' }}
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95 }}
        transition={{ type: 'spring', duration: 0.3, bounce: 0.2 }}
        style={{ background: `linear-gradient(165deg, ${THEME.bgCard} 0%, ${THEME.bgElevated} 100%)`, borderRadius: '24px', width: '95%', maxWidth: '600px', maxHeight: '85vh', overflow: 'hidden', border: `1px solid ${THEME.glassBorder}`, boxShadow: THEME.modalShadow }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ padding: '20px 24px', borderBottom: `1px solid ${THEME.glassBorder}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: `linear-gradient(180deg, ${THEME.bgElevated}80 0%, transparent 100%)` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span style={{ fontSize: '24px' }}>📋</span>
            <h2 style={{ margin: 0, color: THEME.text, fontSize: '20px', fontWeight: '700' }}>Play Queue</h2>
          </div>
          <motion.button
            whileHover={{ scale: 1.1, backgroundColor: THEME.bgActive }}
            whileTap={{ scale: 0.95 }}
            onClick={onClose}
            style={{ background: THEME.bgHover, border: `1px solid ${THEME.glassBorder}`, color: THEME.textMuted, width: '36px', height: '36px', borderRadius: '10px', fontSize: '20px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >×</motion.button>
        </div>

        {/* Tabs */}
        {currentSquad && (
          <div style={{ padding: '12px 24px', borderBottom: `1px solid ${THEME.border}`, display: 'flex', gap: '8px', background: THEME.bg }}>
            <button onClick={() => setActiveTab('mine')} style={{ background: activeTab === 'mine' ? THEME.accent : 'transparent', color: activeTab === 'mine' ? '#000' : THEME.text, border: `1px solid ${activeTab === 'mine' ? THEME.accent : THEME.border}`, borderRadius: '8px', padding: '8px 16px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}>
              My Queue ({queue.length})
            </button>
            <button onClick={() => setActiveTab('squad')} style={{ background: activeTab === 'squad' ? THEME.accent : 'transparent', color: activeTab === 'squad' ? '#000' : THEME.text, border: `1px solid ${activeTab === 'squad' ? THEME.accent : THEME.border}`, borderRadius: '8px', padding: '8px 16px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}>
              Squad Queues
            </button>
          </div>
        )}

        {/* Content */}
        <div style={{ padding: '20px 24px', overflow: 'auto', maxHeight: currentSquad ? 'calc(85vh - 160px)' : 'calc(85vh - 100px)' }}>
          {activeTab === 'mine' ? (
            items.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '48px 20px', color: THEME.textMuted }}>
                <div style={{ fontSize: '48px', marginBottom: '16px', opacity: 0.5 }}>📋</div>
                <p style={{ fontSize: '16px', marginBottom: '8px' }}>Your queue is empty</p>
                <p style={{ fontSize: '13px', opacity: 0.7 }}>Click "+ Add to Queue" on any game to add it here</p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <p style={{ color: THEME.textMuted, fontSize: '12px', margin: '0 0 8px' }}>Drag to reorder • Your #1 is your top pick</p>
                {items.map((item, idx) => renderQueueItem(item, idx, true, true))}
              </div>
            )
          ) : (
            loadingSquad ? (
              <div style={{ textAlign: 'center', padding: '48px' }}><Spinner size={24} /></div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                {squadQueues.map(member => (
                  <div key={member.user_id}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
                      <Avatar src={member.avatar_url} name={member.display_name} size={28} color={member.color || THEME.text} />
                      <span style={{ color: member.color || THEME.text, fontWeight: '600', fontSize: '14px' }}>{member.display_name}</span>
                      <span style={{ color: THEME.textMuted, fontSize: '12px' }}>({member.queue.length} games)</span>
                      {member.user_id === currentUser?.id && <Badge color={THEME.accent} bg={`${THEME.accent}20`}>You</Badge>}
                    </div>
                    {member.queue.length === 0 ? (
                      <div style={{ color: THEME.textMuted, fontSize: '13px', padding: '12px', background: THEME.bg, borderRadius: '10px', textAlign: 'center' }}>No games in queue</div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {member.queue.slice(0, 5).map((item, idx) => (
                          <div key={item.id} style={{ display: 'flex', gap: '10px', alignItems: 'center', padding: '10px 12px', background: THEME.bg, borderRadius: '10px', border: `1px solid ${idx === 0 ? member.color || THEME.accent : THEME.border}30` }}>
                            <span style={{ color: idx === 0 ? member.color || THEME.accent : THEME.textMuted, fontWeight: '700', fontSize: '13px', width: '20px' }}>{idx === 0 ? '🎯' : idx + 1}</span>
                            <img src={item.game.header_image} alt="" style={{ width: '60px', height: '34px', objectFit: 'cover', borderRadius: '6px' }} />
                            <span style={{ color: THEME.text, fontSize: '13px', fontWeight: '500', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.game.name}</span>
                          </div>
                        ))}
                        {member.queue.length > 5 && <div style={{ color: THEME.textMuted, fontSize: '12px', textAlign: 'center' }}>+{member.queue.length - 5} more</div>}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

export default QueueModal;
